import type { ConversationPlacement } from '@crowdex/agent-contracts/api';
import type { GitMetadataGenerator } from '@crowdex/host-kit/renderer/git-actions';
import type { ToolRef } from '@crowdex/plugin-contracts';
import { type ComponentProps, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { ConversationBranchChip } from '@/components/desktop-shell/branch-actions-menu';
import { useChildConversation } from '@/components/desktop-shell/child-conversation';
import { SummaryContent } from '@/components/desktop-summary';
import { useConversationArtifactIndex } from '@/components/desktop-summary/summary-artifacts-section';
import type { SummaryPlace } from '@/components/desktop-summary/summary-environment-section';
import {
  conversationAssistant,
  summaryTasks,
} from '@/components/desktop-summary/summary-refs-sections';
import { useSummaryServers } from '@/components/desktop-summary/use-summary-servers';
import { useShellThreads, useSidebarWaiting } from '@/components/sidebar/shell-navigation';
import { useRefTargets } from '@/features/apps/ref-targets';
import { openAssistant, useWorkspaceDirectory } from '@/lib/assistants/directory';
import { liveTaskStatus } from '@/lib/conversations/live-task-status';
import { useDesktopPreviewState } from '@/lib/desktop-preview-state';
import { useBranchActions } from '@/lib/desktop-shell/branch-actions';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { whileConversationShown } from '@/lib/desktop-shell/conversation-scope';
import { selectSharedFolderPullRequest } from '@/lib/desktop-shell/conversation-worktree';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import { shownConversationState } from '@/lib/desktop-shell/store';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import {
  type NativeWorkspaceAction,
  openNativeWorkspaceIn,
  readNativeGitStatus,
} from '@/lib/native-desktop';
import { showPanelView } from '@/lib/side-panel/panel-tabs';

type SummaryProps = ComponentProps<typeof SummaryContent>;

/** The preview's empty cloud sandbox: a cloud conversation holding no repository. */
const PREVIEW_SANDBOX = '/workspace';

export type ConversationSummaryProps = {
  preview: boolean;
  /** The summary is on screen (pinned beside the transcript, or its popover open). */
  visible: boolean;
  conversationSources: SummaryProps['sources'];
  /** What its tools touched (`conversationRefs` of its messages). */
  conversationRefs?: readonly ToolRef[];
  runtimeMode: SummaryPlace;
  /** A phone: its summary page hands a cloud conversation to one of the user's computers. */
  phone?: boolean;
  /** The agent is working: the conversation does not move meanwhile. */
  runtimeSwitchDisabled?: boolean;
  /** The cloud handoff is under way. */
  cloudHandoffWorking?: boolean;
  /** Hands the conversation over (the shell's `continueOn`). */
  onContinueOn?: (destination: SummaryPlace) => void;
  /** A cloud conversation's "Continue on this computer", after its confirmation. */
  onContinueLocally?: () => void;
  onGenerateGitText?: GitMetadataGenerator;
  onRunAction?: (action: NativeWorkspaceAction) => void;
};

/**
 * The open conversation's summary (D5, mobile 36): its workspace and pull request with what
 * can be done with them (branch, commit, push, pull request), where it runs with its servers,
 * actions, editor and handoff, its artifacts, sources and what wakes its agent. Shown beside the
 * transcript or in a popover (`ThreadSummary`); it opens the side panel's tools, never lives in
 * it.
 */
export function ConversationSummary({
  preview,
  visible,
  conversationSources,
  conversationRefs = [],
  runtimeMode,
  phone = false,
  runtimeSwitchDisabled = false,
  cloudHandoffWorking = false,
  onContinueOn,
  onContinueLocally,
  onGenerateGitText,
  onRunAction,
}: ConversationSummaryProps) {
  const store = useDesktopShellStoreApi();
  const chatId = useDesktopShellStore((state) => state.chatId);
  const revision = useDesktopShellStore((state) => state.messageRevision);
  // What this conversation made, read again as its messages change.
  const { made: artifacts, index: artifactIndex } = useConversationArtifactIndex(
    visible ? chatId : null,
    revision,
  );
  const refTargets = useRefTargets(artifactIndex);
  const opener = useConversationOpener();
  // Its assistant (its channel) and what started it, and the conversations it handed work to.
  const directory = useWorkspaceDirectory(
    useShallow((state) => ({ placements: state.placements, projects: state.projects })),
  );
  const child = useChildConversation(chatId);
  const syncedThreads = useDesktopShellStore((state) => state.syncedThreads);
  // How its tasks stand now, from what the sidebar reads (their conversations, what waits on the
  // user, their runs), so the rows follow them live rather than saying how they started.
  const shellThreads = useShellThreads();
  const waiting = useSidebarWaiting();
  const assistant = conversationAssistant(chatId, directory.placements, directory.projects);
  const tasks = useMemo(() => {
    const placements: Record<string, ConversationPlacement> = { ...directory.placements };
    for (const thread of syncedThreads)
      if (thread.placement !== undefined) placements[thread.id] = thread.placement;
    const children = Object.entries(placements)
      .filter(([, placement]) => placement.parentConversationId === chatId)
      .map(([id, placement]) => ({
        id,
        title: syncedThreads.find((thread) => thread.id === id)?.title ?? 'Task',
        workflow: placement.workflowRun != null && placement.workflowRun.workflowId !== null,
      }));
    return summaryTasks(conversationRefs, children, (id) =>
      liveTaskStatus(
        id,
        shellThreads.find((thread) => thread.id === id),
        waiting.get(id),
        placements[id],
      ),
    );
  }, [conversationRefs, directory.placements, syncedThreads, chatId, shellThreads, waiting]);
  const storeWorkspace = useDesktopShellStore((state) => state.workspace);
  const personalWorkspaceRoot = useDesktopShellStore((state) => state.personalWorkspaceRoot);
  const storeGitStatus = useDesktopShellStore((state) => state.gitStatus);
  const gitStatusState = useDesktopShellStore((state) => state.gitStatusState);
  const cloudRuntime = useDesktopShellStore((state) => state.cloudRuntime);
  const native = useDesktopShellStore((state) => state.native);
  const draftPullRequests = useDesktopShellStore((state) => state.appSettings.draftPullRequests);
  const workspaceOpeners = useDesktopShellStore((state) => state.workspaceOpeners);
  const preferredOpenerId = useDesktopShellStore((state) => state.preferredOpenerId);
  const workspaceEnvironment = useDesktopShellStore((state) => state.workspaceEnvironment);
  const conversationFiles = useDesktopShellStore((state) => state.conversationFiles);
  const sharedFolderPullRequest = useDesktopShellStore(selectSharedFolderPullRequest);
  const moving = useDesktopShellStore(
    (state) =>
      shownConversationState(state).creatingWorktree ||
      shownConversationState(state).handingOffWorktree ||
      state.localHandoffWorking,
  );
  const setSidePanelOpen = useDesktopShellStore((state) => state.setSidePanelOpen);
  const setPanelTabs = useDesktopShellStore((state) => state.setPanelTabs);
  const setMessageReviewDiff = useDesktopShellStore((state) => state.setMessageReviewDiff);
  const setPreferredOpenerId = useDesktopShellStore((state) => state.setPreferredOpenerId);
  const setLocalHandoffOpen = useDesktopShellStore((state) => state.setLocalHandoffOpen);
  const { createBranch } = useBranchActions();
  // The preview's boards of a cloud conversation, with a repository or an empty sandbox.
  const previewInCloud = useDesktopPreviewState((state) => preview && state.inCloud);
  const previewSandbox = useDesktopPreviewState((state) => preview && state.emptySandbox);
  const place: SummaryPlace = previewInCloud || previewSandbox ? 'cloud' : runtimeMode;
  const workspace = previewSandbox ? PREVIEW_SANDBOX : storeWorkspace;
  // A cloud conversation's git is its agent's: this computer reads none of it.
  const gitStatus = previewSandbox || previewInCloud ? null : storeGitStatus;
  const servers = useSummaryServers({ visible, preview, workspace, cloudRuntime });
  const ide =
    workspaceOpeners.find((opener) => opener.id === preferredOpenerId) ??
    workspaceOpeners[0] ??
    null;

  return (
    <SummaryContent
      artifactIndex={artifactIndex}
      artifacts={artifacts}
      assistant={{
        assistant,
        child,
        isChannel: assistant !== null && assistant.conversationId === chatId,
        onOpenAssistant: () => {
          if (assistant !== null && opener !== null)
            void openAssistant(assistant, (id) => void opener.openConversation(id));
        },
        onOpenParent: (id) => void opener?.openConversation(id),
      }}
      branchControl={<ConversationBranchChip align="end" shortcut={false} />}
      git={{
        gitStatusState,
        draftPullRequests,
        conversationFiles,
        onGenerateGitText,
        onCreateBranch: createBranch ?? undefined,
        onGitStatusChange: async () => {
          // A refresh after an action: the git status sync reports a folder that cannot be read.
          if (workspace === null || preview) return;
          const status = await readNativeGitStatus(workspace).catch(() => undefined);
          // Its conversation's status: another shown meanwhile keeps its own.
          if (status !== undefined)
            whileConversationShown(store, chatId, (state) => state.setGitStatus(status));
        },
      }}
      gitStatus={gitStatus}
      handoff={
        onContinueOn === undefined || onContinueLocally === undefined
          ? undefined
          : {
              onContinueOn,
              // A phone hands it to one of the user's computers (45), as its ⋮ does.
              onContinueLocally: phone ? () => setLocalHandoffOpen(true) : onContinueLocally,
              phone,
              canSendToCloud: gitStatus !== null,
              disabled: runtimeSwitchDisabled,
              pending: moving || cloudHandoffWorking,
            }
      }
      hidden={!visible}
      // In this conversation's side panel, on its item.
      onOpenArtifact={(artifact) => dockArtifact(store, artifact.id)}
      onOpenTask={(task) => void opener?.openConversation(task.id)}
      onOpenPanel={(panel) => {
        if (panel === 'review') setMessageReviewDiff(null);
        setPanelTabs((tabs) => showPanelView(tabs, panel));
        setSidePanelOpen(true);
      }}
      place={place}
      preview={preview}
      pullRequestClaim={preview || place === 'cloud' ? undefined : sharedFolderPullRequest}
      refTargets={refTargets}
      refs={conversationRefs}
      repository={workspace !== null && workspace !== personalWorkspaceRoot}
      running={place === 'cloud' && (cloudRuntime !== null || previewInCloud)}
      servers={servers}
      sources={conversationSources}
      taskId={chatId}
      tasks={tasks}
      tools={{
        actions: native === true ? (workspaceEnvironment?.actions ?? []) : [],
        onRunAction,
        ide: native === true ? ide : null,
        onOpenInIde: (openerId) => {
          if (workspace === null) return;
          setPreferredOpenerId(openerId);
          if (!preview) void openNativeWorkspaceIn(workspace, openerId);
        },
      }}
      workspace={workspace}
    />
  );
}
